<script setup lang="ts">
import {
  getDate,
  getTime,
  getShortMonth,
  getDayOfWeek,
} from "~/modules/helpers/day";

const router = useRouter();
const route = useRoute();
const { $get } = useAxios();

const now = new Date();
// const isLoaded= ref(false)
const { data: perform, pending: isPerformLoaded } = await useAsyncData(
  `get perform with id - ${route.params.id}`,
  () => $get(`/api/v3/perform/${route.params.id}`)
);
const { data: posters, pending: isPostersLoaded } = await useAsyncData(
  `get 3 active posters by perform with id - ${route.params.id}`,
  () =>
    $get(
      `/api/v3/poster?perform_in=${
        route.params.id
      }&_sort=date:asc&date_gt=${new Date(
        new Date(now).setHours(new Date(now).getHours() - 1)
      ).toISOString()}&_limit=3`
    )
);

const hall = computed(
  () =>
    perform.value?.hall ?? {
      name: "Большая сцена",
      to: "main-hall",
      icon: "main-hall",
      icon_rounded: "main-hall-rounded",
    }
);
const prevText = computed(() => {
  try {
    if (router.options.history.state.back === "/poster") {
      return "Вся афиша";
    }
    switch (router.options.history.state.back) {
      case "/poster":
        return "Вся афиша";
      case "/theater/performs":
        return "Все спектакли";
      default:
        return "Назад";
    }
  } catch (error) {
    return "Назад";
  }
});
const color = computed(async () => {
  const f = await fetch(
    `https://ucarecdn.com/${perform.value?.img.fileKey}/-/preview/-/main_colors/`
  );
  const data = await f.json();

  return `rgba(${data.main_colors[1].join(",")}, 1)`;
});

const head = computed(() => {
  return {
    title: `${perform.value?.name} | Государственный русский театр драмы им. Ф. А. Искандера`,
    meta: [
      {
        hid: "title",
        property: "title",
        content: `${perform.value?.name} | Государственный русский театр драмы им. Ф. А. Искандера`,
      },
      {
        hid: "og:title",
        property: "og:title",
        content: `${perform.value?.name} | Государственный русский театр драмы им. Ф. А. Искандера`,
      },
      {
        hid: "url",
        name: "url",
        content: `https://rusdram.org/theater/performs/${perform.value?._id}`,
      },
      {
        hid: "og:url",
        property: "og:url",
        content: `https://rusdram.org/theater/performs/${perform.value?._id}`,
      },
      {
        hid: "image",
        name: "image",
        content: `https://ucarecdn.com/${perform.value?.img?.fileKey}/-/quality/smart/image.png`,
      },
      {
        name: "og:image",
        property: "og:image",
        content: `https://ucarecdn.com/${perform.value?.img?.fileKey}/-/quality/smart/image.png`,
      },
    ],
  };
});

useHead(head);
</script>

<template>
  <nuxt-layout name="default">
    <div v-if="!(isPerformLoaded && isPostersLoaded)">
      <r-section v-if="perform && posters">
        <div class="flex flex-row flex-nowrap gap-x-10 relative">
          <button
            @click="$router.go(-1)"
            class="flex flex-row justify-items-center focus:outline-none"
          >
            <span class="text-light-gray text-2xl pt-1/2">
              <RIcon class="h-4 w-2" icon="angle-left" />
            </span>
            <span class="pl-4 pb-px -mt-1 text-light-gray text-xl">{{
              prevText
            }}</span>
          </button>
        </div>
        <div class="grid grid-cols-12 gap-7 mt-5">
          <section
            class="col-start-1 col-end-13 xl:col-end-7 rounded-xl overflow-hidden h-full relative z-0"
          >
            <img
              loading="lazy"
              class="w-full h-full object-cover"
              :src="`https://ucarecdn.com/${perform?.img.fileKey}/-/format/auto/-/quality/smart/image.png`"
              :alt="perform.name"
            />
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 rounded-md text-white text-lg font-semibold absolute z-10 top-4 right-4 glass"
              style="transform: rotate(64deg)"
            >
              <p
                class="pt-1.5"
                :class="[perform.age_limit.length > 2 ? '' : 'pl-1']"
                :style="`color:${color}`"
                style="transform: rotate(-64deg)"
              >
                {{ perform.age_limit }}
              </p>
            </div>
          </section>
          <section class="col-start-1 xl:col-start-7 col-end-13 flex flex-col">
            <div class="flex flex-row space-x-5 xl:mt-2">
              <h2
                class="font-sans text-4xl w-4/6 sm:w-auto text-dark-gray leading-none my-auto pt-0.5"
              >
                {{ perform.name }}
              </h2>
              <r-button
                v-if="perform.isPremiere"
                size="sm"
                class="my-auto text-sm"
                cursor="default"
                >Премьера</r-button
              >
            </div>
            <div class="flex flex-row space-x-4 pt-2">
              <p class="font-sans font-regular text-akcent text-lg">
                {{ perform.genre }}
              </p>
            </div>

            <r-perform-staging
              class="mt-6"
              :author="perform?.author"
              :staging="perform.staging"
            />

            <div
              class="flex flex-col xl:flex-row space-y-2 xl:space-y-0 xl:space-x-7 mt-7"
            >
              <nuxt-link v-if="hall" :to="`/theater/halls/${hall.to}`">
                <div
                  class="flex flex-row space-x-2 xl:justify-center xl:justify-items-center"
                >
                  <div>
                    <RIcon :icon="hall.icon" class="h-10 w-10"></RIcon>
                  </div>
                  <div class="my-auto h-5 pt-px">
                    <p class="font-sans font-medium text-dark-gray text-lg">
                      {{ hall.name }}
                    </p>
                  </div>
                </div>
              </nuxt-link>
              <nuxt-link v-else :to="`/theater/halls/main-hall`">
                <div
                  class="flex flex-row space-x-2 xl:justify-center xl:justify-items-center"
                >
                  <div>
                    <RIcon icon="main-hall" class="h-10 w-10"></RIcon>
                  </div>
                  <div class="my-auto h-5 pt-px">
                    <p class="font-sans font-medium text-dark-gray text-lg">
                      Большая сцена
                    </p>
                  </div>
                </div>
              </nuxt-link>
              <div
                class="flex flex-row space-x-2 xl:justify-center xl:justify-items-center"
              >
                <div>
                  <RIcon icon="duration-grey" class="h-10 w-10"></RIcon>
                </div>
                <div class="my-auto h-5 pt-px">
                  <p class="font-sans font-medium text-dark-gray text-lg">
                    {{ perform.time }}
                  </p>
                </div>
              </div>
            </div>

            <div class="flex flex-col mt-7 space-y-1">
              <p
                v-for="poster in posters"
                :key="poster.id"
                class="text-primary text-lg font-sans"
              >
                {{
                  `${getDate(poster.date)}
                        ${getShortMonth(poster.date)}
                        ${getTime(poster.date)},
                        ${getDayOfWeek(poster.date)}`
                }}
              </p>
            </div>

            <div class="flex flex-col pt-7 mt-auto">
              <RPerformBuy
                v-if="posters.length"
                :to="`/buy/${perform._id}/${posters[0]._id}`"
                :text="perform.price"
              />
            </div>
          </section>

          <RPerformTroupe
            v-if="perform.troupe.length"
            class="col-start-1 col-end-13"
            :troupe="perform.troupe"
          />

          <RPerformDescription
            class="col-start-1 col-end-13 lg:col-end-7"
            :description="perform.description"
            :premiere="perform.premiere"
          />
          <RPerformPress
            :press="perform.press"
            class="col-start-1 lg:col-start-7 col-end-13"
          />
          <RPerformReview
            class="col-start-1 col-end-13"
            :reviews="perform.reviews"
          ></RPerformReview>
          <RPerformMedia
            class="col-start-1 col-end-13"
            :video="perform.video"
            :album="perform.album"
          ></RPerformMedia>
          <div class="col-start-1 col-end-13">
            <r-share></r-share>
          </div>
        </div>
      </r-section>
    </div>
    <r-full-page-pro-loader v-else></r-full-page-pro-loader>
  </nuxt-layout>
</template>

<style lang="scss" scoped>
.glass {
  backdrop-filter: blur(30px) brightness(1.15) opacity(1);
}
</style>
